import React, { useMemo } from 'react';
import { Box, Typography, Paper, ToggleButton, ToggleButtonGroup, Chip, Tooltip, Collapse, IconButton } from '@mui/material';
import { ORGANIZATION_FIELDS_METADATA } from '../../config/userUploaderConfig';
import FieldMappingRow, { FieldMappingRowValue } from './FieldMappingRow';
import { smartMatch } from '../../utils/csvSmartMatch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export type FieldMappings = Record<string, FieldMappingRowValue>;

interface FieldMappingGridProps {
  csvHeaders: string[];
  csvSampleData?: Record<string, string>[];
  mappings: FieldMappings;
  onMappingsChange: (mappings: FieldMappings) => void;
  isDeleteMode?: boolean;
  onExistingActionChange?: (action: 'skip' | 'update') => void;
  existingAction?: 'skip' | 'update';
}

const FieldMappingGrid: React.FC<FieldMappingGridProps> = ({
  csvHeaders,
  csvSampleData,
  mappings,
  onMappingsChange,
  isDeleteMode = false,
  onExistingActionChange,
  existingAction = 'skip',
}) => {
  const visibleFields = useMemo(
    () => ORGANIZATION_FIELDS_METADATA.filter((f) => !f.hidden),
    []
  );

  const deleteModeFields = useMemo(
    () => visibleFields.filter((f) => f.key === 'idNumber' || f.key === 'idTypeId' || f.key === 'name'),
    [visibleFields]
  );

  const sortedFieldsToShow = useMemo(() => {
    const baseFields = isDeleteMode ? deleteModeFields : visibleFields;
    return [...baseFields].sort((a, b) => {
      const aEnabled = mappings[a.key as string]?.enabled ?? a.requiredApi;
      const bEnabled = mappings[b.key as string]?.enabled ?? b.requiredApi;
      if (aEnabled === bEnabled) return 0;
      return aEnabled ? -1 : 1;
    });
  }, [isDeleteMode, deleteModeFields, visibleFields, mappings]);

  const matchedCsvColumns = useMemo(() => {
    const matched = new Set<string>();
    for (const key of Object.keys(mappings)) {
      const m = mappings[key];
      if (m.enabled) {
        if (m.csvColumn) matched.add(m.csvColumn);
        if (m.csvColumns) m.csvColumns.forEach((c) => matched.add(c));
      }
    }
    return matched;
  }, [mappings]);

  const unmatchedCsvColumns = useMemo(
    () => csvHeaders.filter((h) => !matchedCsvColumns.has(h)),
    [csvHeaders, matchedCsvColumns]
  );

  const unmappedRequiredFields = useMemo(
    () => sortedFieldsToShow.filter((f) => {
      if (!f.requiredApi) return false;
      const m = mappings[f.key as string];
      return !m || !m.enabled || (!m.csvColumn && !m.csvColumns?.length && m.customValue === undefined);
    }),
    [sortedFieldsToShow, mappings]
  );

  const [showUnmatched, setShowUnmatched] = React.useState(true);

  const handleRowChange = (key: string, value: FieldMappingRowValue) => {
    onMappingsChange({ ...mappings, [key]: value });
  };

  const handleAutoMatch = () => {
    const fieldKeys = visibleFields.map((f) => f.key as string);
    const multiColumnFields = visibleFields
      .filter((f) => f.parser === 'multi_column')
      .map((f) => f.key as string);

    const result = smartMatch(csvHeaders, fieldKeys, { multiColumnFields });
    const newMappings: FieldMappings = { ...mappings };

    for (const field of visibleFields) {
      const key = field.key as string;
      if (!newMappings[key]) {
        newMappings[key] = {
          enabled: field.requiredApi,
          csvColumn: null,
          csvColumns: null,
          customValue: field.defaultValue,
        };
      }
    }

    for (const match of result.singleMatches) {
      if (newMappings[match.fieldKey]) {
        newMappings[match.fieldKey] = {
          ...newMappings[match.fieldKey],
          csvColumn: match.csvColumn,
          enabled: true,
          matchConfidence: match.confidence,
        };
      }
    }

    for (const match of result.multiColumnMatches) {
      if (newMappings[match.fieldKey]) {
        newMappings[match.fieldKey] = {
          ...newMappings[match.fieldKey],
          csvColumns: match.csvColumns,
          enabled: true,
          matchConfidence: match.confidence,
        };
      }
    }

    if (result.coordinateMatch) {
      const latMapping = newMappings['latitude'];
      const longMapping = newMappings['longitude'];
      if (latMapping) {
        latMapping.csvColumn = result.coordinateMatch.csvColumn;
        latMapping.enabled = true;
        latMapping.matchConfidence = result.coordinateMatch.confidence;
        latMapping.isCoordinateSplit = true;
      }
      if (longMapping) {
        longMapping.csvColumn = result.coordinateMatch.csvColumn;
        longMapping.enabled = true;
        longMapping.matchConfidence = result.coordinateMatch.confidence;
        longMapping.isCoordinateSplit = true;
      }
    }

    onMappingsChange(newMappings);
  };

  React.useEffect(() => {
    if (csvHeaders.length > 0) {
      handleAutoMatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvHeaders]);

  return (
    <Paper variant="outlined" sx={{ p: 0 }}>
      {!isDeleteMode && onExistingActionChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant="body2">
            Cuando una organizacion ya existe:
          </Typography>
          <ToggleButtonGroup
            value={existingAction}
            exclusive
            size="small"
            onChange={(_, val) => val && onExistingActionChange(val as 'skip' | 'update')}
          >
            <ToggleButton value="skip">Omitir</ToggleButton>
            <ToggleButton value="update">Actualizar</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {(unmatchedCsvColumns.length > 0 || unmappedRequiredFields.length > 0) && (
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              bgcolor: unmappedRequiredFields.length > 0 ? 'error.light' : 'warning.light',
              opacity: 0.9,
            }}
          >
            {unmappedRequiredFields.length > 0 ? (
              <WarningAmberIcon color="error" fontSize="small" />
            ) : (
              <WarningAmberIcon color="warning" fontSize="small" />
            )}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {unmappedRequiredFields.length > 0
                ? `${unmappedRequiredFields.length} campo obligatorio sin asignar`
                : `${unmatchedCsvColumns.length} columna CSV sin asignar`}
            </Typography>
            {unmatchedCsvColumns.length > 0 && unmappedRequiredFields.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                ({csvHeaders.length - unmatchedCsvColumns.length} de {csvHeaders.length} columnas asignadas)
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={() => setShowUnmatched((s) => !s)}
              sx={{ ml: 'auto' }}
            >
              <ExpandMoreIcon sx={{ transform: showUnmatched ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
            </IconButton>
          </Box>
          <Collapse in={showUnmatched}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1.5, py: 1 }}>
              {unmappedRequiredFields.map((f) => (
                <Tooltip key={f.key as string} title={`El campo obligatorio "${f.displayName}" no tiene columna CSV ni valor por defecto`}>
                  <Chip
                    label={f.displayName}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Tooltip>
              ))}
              {unmatchedCsvColumns.map((col) => (
                <Tooltip key={col} title={`La columna CSV "${col}" no esta asignada a ningun campo`}>
                  <Chip
                    label={col}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Tooltip>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {unmatchedCsvColumns.length === 0 && unmappedRequiredFields.length === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: 'success.light', opacity: 0.7, borderBottom: '1px solid', borderColor: 'divider' }}>
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="body2" color="success.dark">
            Las {csvHeaders.length} columnas CSV estan asignadas ({csvHeaders.length - unmatchedCsvColumns.length} en uso)
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(180px, 1fr) minmax(200px, 1.5fr) minmax(140px, 1fr)',
          gap: 1.5,
          px: 1,
          py: 1,
          bgcolor: 'grey.100',
          borderBottom: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" fontWeight={700} color="textSecondary">
          Campo
        </Typography>
        <Typography variant="caption" fontWeight={700} color="textSecondary">
          Columna CSV
        </Typography>
        <Typography variant="caption" fontWeight={700} color="textSecondary">
          {isDeleteMode ? '' : 'Valor por defecto'}
        </Typography>
      </Box>

      <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
        {sortedFieldsToShow.map((field) => (
          <FieldMappingRow
            key={field.key}
            field={field}
            csvHeaders={csvHeaders}
            csvSampleData={csvSampleData}
            value={
              mappings[field.key as string] || {
                enabled: field.requiredApi,
                csvColumn: null,
                csvColumns: null,
                customValue: field.defaultValue,
              }
            }
            onChange={(val) => handleRowChange(field.key as string, val)}
          />
        ))}
      </Box>

      {isDeleteMode && (
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'error.light', opacity: 0.8 }}>
          <Typography variant="caption" color="error.dark">
            Modo eliminacion: solo se necesita Numero de ID y Tipo de ID para buscar las organizaciones a eliminar.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FieldMappingGrid;
